/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useEffect, useMemo } from "react"

import _ from "lodash"
import Button from "./generic/buttons/Button"

import SeasonDatePicker from "./SeasonDatePicker"
import AirportSeasonSelector from "./AirportSeasonSelector"
import {
  AirportSeasonConfiguration,
  AirportSeasonConfigurations
} from "../api/AirportSeasonConfiguration"

import {
  TerminalDropdownField,
  AvailabilityTypeField,
  ValueTypeField,
  AdtField,
  DoopField,
  HourField,
  ScheduleField,
  OperatorDropdownField,
  OperatorField
} from "./selectorFields"

import { CheckBooleanField } from "./generic/form/CheckBooleanField"
import { FieldName, FlightText } from "../common/text"
import { IExcludeFieldConfig } from "../pages/Config"
import { IMainFlightSelectorProps } from "./FlightSelector"
import { useAppSelector } from "../app/hooks"
import Card from "./generic/Card"
import { constraintQuotaValues, ConstraintQuotaValue } from "./Utils"

interface IAvailabilitySelectorProps extends IMainFlightSelectorProps {
  onChange: (
    field: string,
    value: string | boolean | string[],
    isValid: boolean
  ) => void
  values: IAvailabilitySelectorValues
  excludeFields: IExcludeFieldConfig
  codeType: string
  title: string
}

export interface IAvailabilitySelectorValues {
  [FieldName.constraintType]: string
  [FieldName.valueType]: string
  [FieldName.operators]: string[]
  [FieldName.terminal]: string
  [FieldName.adt]: string
  [FieldName.dayOfWeek]: string
  [FieldName.hour]: string
  [FieldName.date]: string
  [FieldName.useHourlyConstraints]: boolean
  [FieldName.mergeTotals]: boolean
  [FieldName.overlayQuotaConstraints]: boolean
  [FieldName.schedule]: string
}

const AvailabilitySelector: React.FunctionComponent<
  IAvailabilitySelectorProps
> = ({
  airportSeasons,
  onChange,
  values,
  codeType,
  airportseasonSelectorConfig,
  excludeFields,
  touched,
  hasError,
  errorMsg,
  emptyMatrix,
  title
}) => {
  const { season, airports, useICAOCode } = useAppSelector(
    (state) => state["selectionReducer"].airportSeasonsSelector
  )
  const handleFieldChange = (name: string, value: any, error: boolean) => {
    onChange(name, value, error)
    if (constraintQuotaValues.includes(value)) {
      values.overlayQuotaConstraints = false
    }

    emptyMatrix()
  }

  const peakWeekDateClicked = () => {
    if (config) {
      handleFieldChange("date", config.peakWeekDate, false)
    }
  }

  // get configuration for selected season and airports
  const getConfiguration = (): AirportSeasonConfiguration => {
    const airportSeasonsClone = _.cloneDeep(airportSeasons)
    const configs: AirportSeasonConfigurations = _.filter(
      airportSeasonsClone,
      (rec) => {
        return (
          rec.season === season &&
          _.findIndex(airports, (a) => {
            return a === rec.airport
          }) !== -1 &&
          (useICAOCode ? rec.supportsIcao : rec.supportsIata)
        )
      }
    )

    if (configs.length > 0) {
      let actualConfig: AirportSeasonConfiguration = configs[0]

      actualConfig.supportsQuotaOverlay = configs.find(
        (c) => c.supportsQuotaOverlay
      )
        ? true
        : false

      actualConfig.terminals = _.chain(configs)
        .reduce((result, item) => {
          return _.intersection(result, item.terminals)
        }, configs[0].terminals)
        .uniq()
        .sort()
        .value()

      return actualConfig
    } else {
      return null
    }
  }

  const config = getConfiguration()

  const valueTypeOpts = useMemo(() => {
    let opts = [
      { label: "Availability", value: "availability" },
      { label: "Load/Capacity", value: "loadCapacity" },
      { label: "Held Slots", value: "heldSlots" }
    ]

    if (config && config.unsupportedTypes) {
      _.map(config.unsupportedTypes, (o) => {
        if (o.constraintType === values.constraintType) {
          opts = _.filter(opts, (a) => {
            return a.value !== o.valueType
          })
        }
      })
    }

    return opts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  useEffect(() => {
    const valueExist = valueTypeOpts.find(
      (opt) => opt.value === values.valueType
    )
    if (!valueExist) {
      onChange("valueType", valueTypeOpts[0].value, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueTypeOpts])

  useEffect(() => {
    if (values.operators.length > 0) {
      onChange("operators", [], true)
    }
  }, [values.valueType])

  const scheduleOpts = useMemo(() => {
    return config && config.supportsBaseline
      ? [
          { label: "Live", value: "live" },
          { label: "Baseline", value: "baseline" }
        ]
      : [{ label: "Live", value: "live" }]
  }, [config])

  useEffect(() => {
    if (config && !config.supportsBaseline) {
      onChange("schedule", scheduleOpts[0]?.value, false)
    }
  }, [scheduleOpts, onChange, config])

  const terminalOpts = useMemo(() => {
    return config && config.terminals
      ? _.map(config.terminals, (o) => {
          return { value: o, label: o }
        })
      : []
  }, [config])

  useEffect(() => {
    if (
      config &&
      !excludeFields?.terminal &&
      !constraintQuotaValues.includes(values.constraintType) &&
      (!values.terminal || !config.terminals.includes(values.terminal))
    ) {
      onChange("terminal", terminalOpts[0]?.value ?? "", false)
    }
  }, [config, excludeFields.terminal, values.terminal, terminalOpts, onChange])

  useEffect(() => {
    if (config && excludeFields?.terminal) {
      onChange("terminal", "", false)
    }
  }, [config, excludeFields.terminal, onChange])

  const constraintOpts = useMemo(() => {
    let a = [{ label: "Runway", value: "Runway" }]
    if (terminalOpts.length) {
      a = a.concat({
        label: config?.country !== "ES" ? "Pax" : FlightText.terminal,
        value: "Pax"
      })
    }

    if (config && config.supportsQuotaConstraints) {
      const quotaConstraintOpts = [
        { label: "Quota Points", value: ConstraintQuotaValue.QuotaPoints },
        {
          label: "Quota Movements",
          value: ConstraintQuotaValue.QuotaMovements
        },
        { label: "Quota Seats", value: ConstraintQuotaValue.QuotaSeats }
      ]
      return [...a, ...quotaConstraintOpts]
    }

    return a
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminalOpts])

  useEffect(() => {
    if (
      (config && config.terminals.length === 0) ||
      (config &&
        !constraintOpts.find((opt) => opt.value === values.constraintType))
    ) {
      onChange("constraintType", constraintOpts[0]?.value, false)
    }
  }, [config, constraintOpts, onChange])

  //update operators if restricted changes
  useEffect(() => {
    let operatorRestricted: boolean = config && config.operatorRestricted
    const operators = _.cloneDeep(values.operators)

    const updOperators = operators.length
      ? operatorRestricted
        ? operators.filter((val) =>
            config ? config.operators.find((o) => val === o) : false
          )
        : operators.length > 1
        ? operators.splice(0, 1)
        : operators
      : []

    if (updOperators && !_.isEqual(values.operators, updOperators)) {
      handleFieldChange("operators", updOperators, true)
    }
  }, [airports])

  const getOperator = () => {
    const isOperatorRestricted = config && config.operatorRestricted
    const opts =
      config && config.operatorRestricted
        ? _.map(config.operators, (o) => {
            return { value: o, label: o }
          })
        : []

    return (
      <Fragment>
        <div className="ocs-w-96 ocs-mr-12">
          {isOperatorRestricted ? (
            <OperatorDropdownField
              isMulti
              value={values.operators}
              options={opts}
              clearable={false}
              onChange={handleFieldChange}
              errorMsg={errorMsg}
              menuPortalTarget={document.body}
            />
          ) : (
            <OperatorField
              value={values.operators && values.operators[0]}
              onChange={(name, val, error) => {
                if (typeof val === "string") {
                  val = val.trim()
                }
                handleFieldChange(name, val !== "" && val ? [val] : [], error)
              }}
            />
          )}
        </div>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <div className="ocs-selector-container-heading d-flex align-items-center justify-content-between">
        <h2 className={`ocs-heading`}>{title}</h2>
      </div>
      <Card title={FlightText.airportSeson}>
        <div className="ocs-mb-12">
          <AirportSeasonSelector
            airportSeasons={airportSeasons}
            handleChange={handleFieldChange}
            airportseasonSelectorConfig={airportseasonSelectorConfig}
            codeType={codeType}
            hasError={hasError}
            errorMsg={errorMsg}
            emptyMatrix={emptyMatrix}
          />
        </div>
      </Card>

      <Card title={FlightText.availability}>
        <div className="d-flex flex-column">
          <div className="d-flex ocs-mb-12">
            <div className="ocs-w-160 ocs-mr-12">
              <AvailabilityTypeField
                value={values.constraintType}
                options={constraintOpts}
                onChange={handleFieldChange}
              />
            </div>
            {!excludeFields?.terminal && (
              <div className="ocs-w-76">
                <TerminalDropdownField
                  clearable={constraintQuotaValues.includes(
                    values.constraintType
                  )}
                  options={terminalOpts}
                  value={values.terminal}
                  onChange={handleFieldChange}
                />
              </div>
            )}
          </div>
          <div className="d-flex ocs-mb-12">
            <div className="ocs-w-136 ocs-mr-12">
              <ValueTypeField
                value={values.valueType}
                options={valueTypeOpts}
                onChange={handleFieldChange}
              />
            </div>
            {values.valueType === "heldSlots" && getOperator()}
            <div className="ocs-w-108 ocs-mr-12">
              <ScheduleField
                value={values.schedule}
                onChange={handleFieldChange}
                options={scheduleOpts}
              />
            </div>
          </div>
          <div className="d-flex ocs-mb-12">
            <div className="ocs-mr-12">
              <CheckBooleanField
                name={FieldName.mergeTotals}
                value={values.mergeTotals ? values.mergeTotals : false}
                caption={"Merge Totals"}
                onChange={handleFieldChange}
              />
            </div>
            {!excludeFields?.overlayQuotaConstraints && (
              <div className="ocs-mr-12">
                <CheckBooleanField
                  name={FieldName.overlayQuotaConstraints}
                  value={values.overlayQuotaConstraints}
                  caption={"Overlay Quota Constraints"}
                  onChange={handleFieldChange}
                />
              </div>
            )}
            {!excludeFields?.useHourlyConstraints && (
              <CheckBooleanField
                name={FieldName.useHourlyConstraints}
                value={values.useHourlyConstraints}
                caption={"Use Hourly Constraints"}
                onChange={handleFieldChange}
              />
            )}
          </div>
        </div>
      </Card>

      <Card title={FlightText.period}>
        <div className="d-flex flex-column">
          <div className="d-flex ocs-mb-12">
            {!excludeFields?.adt && (
              <div className="ocs-w-80 ocs-mr-12">
                <AdtField value={values.adt} onChange={handleFieldChange} />
              </div>
            )}
            {!excludeFields?.adt && (
              <div className="ocs-mr-12 ocs-w-80">
                <DoopField
                  value={values.dayOfWeek}
                  onChange={handleFieldChange}
                />
              </div>
            )}
            {!excludeFields?.date && (
              <div className="d-flex ocs-mr-12">
                <div className="ocs-w-128 ocs-mr-4">
                  <SeasonDatePicker
                    required
                    name={FieldName.date}
                    caption="Week Containing"
                    showIcon
                    value={values.date}
                    isEmpty={values.date === null}
                    onChange={handleFieldChange}
                    touched={touched}
                  />
                </div>
                {!excludeFields?.peakWeek && (
                  <div className="ocs-peak-week-btn">
                    <Button onClick={peakWeekDateClicked}>Peak Week</Button>
                  </div>
                )}
              </div>
            )}
            {!excludeFields?.hour && (
              <div className="ocs-w-68">
                <HourField value={values.hour} onChange={handleFieldChange} />
              </div>
            )}
          </div>
        </div>
      </Card>
    </Fragment>
  )
}
export default AvailabilitySelector
